<template>
  <div
    class="ao-survey"
    :id="surveyAnchorId"
  >
    <div v-if="props.payload?.data?.otazky" class="ao-survey__content">
      <h2 class="h5">{{getTitle()}}</h2>

      <!-- Submit success -->
      <div v-if="submitSuccess" class="messages">
        <DefaultIconsCheck class="messages__icon" />
        <h2 class="h3" v-html="$t('artobjects.survey_submit_success_title')" />
        <p class="messages__subtitle" v-html="$t('artobjects.survey_submit_success_subtitle')" />
      </div>
      <!-- Form -->
      <UForm v-else :validate="validate" :state="state" class="space-y-4" @submit="onSubmit">
        <fieldset v-for="(question, index) in getQuestions()" :key="`question_${index}`" :id="`${surveyAnchorId}.question.${question.id}`" class="anchor">
          <legend>{{question.question}}</legend>

          <!-- checkboxes -->
          <UFormGroup
            v-if="state?.answers[question?.id]?.constructor === Object"
            :name="`question.${question?.id}`"
            required
          >
            <ul class="answers">
              <li v-for="(answer, aindex) in question.answers" :key="`answer_${index}_${aindex}`">
                <label :for="`answer_${question.id}_${aindex}`">
                  <input v-model="state.answers[question?.id][answer.value]" type="checkbox" :id="`answer_${question.id}_${aindex}`" class="answer" :value="answer.value" :name="question.id" :checked="answer.value == state.answers[question?.id]" :disabled="isDisabled">
                  {{ answer.label }}
                </label>
              </li>
            </ul>
          </UFormGroup>
          <!-- radio -->
          <UFormGroup
            v-else
            :name="`question.${question?.id}`"
            required
          >
            <ul class="answers">
              <li v-for="(answer, aindex) in question.answers" :key="`answer_${index}_${aindex}`">
                <label :for="`answer_${question.id}_${aindex}`">
                  <input v-model="state.answers[question?.id]" type="radio" :id="`answer_${question.id}_${aindex}`" class="answer" :value="answer.value" :name="question.id" :checked="answer.value == state.answers[question?.id]" :disabled="isDisabled">
                  {{ answer.label }}
                </label>
              </li>
            </ul>
          </UFormGroup>
        </fieldset>

        <h3 class="h3">{{ $t('artobjects.survey_contact_title')}}</h3>

        <UFormGroup :label="$t('artobjects.survey_fullname')" name="fullname" required :ui="{label:{wrapper: 'input'}}" :id="`${surveyAnchorId}.fullname`" class="anchor">
          <UInput v-model="state.fullname" :disabled="isDisabled" />
        </UFormGroup>

        <UFormGroup :label="$t('artobjects.survey_email')" name="email" required :ui="{label:{wrapper: 'input'}}" :id="`${surveyAnchorId}.email`" class="anchor">
          <UInput v-model="state.email" :disabled="isDisabled" />
        </UFormGroup>

        <UFormGroup name="terms" required>
          <UCheckbox
            v-model="state.consents.terms"
            name="terms"
            :disabled="isDisabled"
            :id="`${surveyAnchorId}.terms`"
            class="anchor"
          >
            <template #label>
              <span class="input" v-html="$t('artobjects.survey_consent_terms')" />
              <span class="required">*</span>
            </template>
          </UCheckbox>

        </UFormGroup>

        <UFormGroup name="gdpr">
          <UCheckbox
            v-model="state.consents.gdpr"
            input-class="input"
            :disabled="isDisabled"
            :id="`${surveyAnchorId}.gdpr`"
            class="anchor"
          >
            <template #label>
              <span class="input" v-html="$t('artobjects.survey_consent_gdpr')" />
            </template>
          </UCheckbox>
        </UFormGroup>

        <!-- Already submitted -->
        <div v-if="isSurveySubmitted" class="messages messages--already_submitted">
          <h2 class="h4" v-html="$t('artobjects.survey_already_submitted_title')" />
          <p v-html="$t('artobjects.survey_already_submitted_subtitle') " />
        </div>

        <div
          v-if="submitErrors.length"
          v-for="error in submitErrors"
          class="error"
        >
          {{ $t(`validation.${error.code}`) }}
        </div>

        <div v-if="submitError" class="messages messages--error">
          <h2 class="h3" v-html="$t('artobjects.survey_submit_error_title')" />
          <p class="messages__subtitle" v-html="$t('artobjects.survey_submit_error_subtitle')" />
        </div>

        <UButton type="submit" class="btn btn--lg" :disabled="isDisabled" :loading="isLoading">
          {{ $t('artobjects.survey_submit') }}
        </UButton>
      </UForm>
    </div>
    <div v-else class="messages">
      <h2 class="h3">{{ $t('artobjects.survey_ended_title') }}</h2>
      <p class="messages__subtitle">{{ $t('artobjects.survey_ended_subtitle') }}</p>
    </div>
  </div>
</template>


<script setup lang="ts">
import type { FormError, FormSubmitEvent } from '#ui/types'
import { useI18n } from "vue-i18n"
import {useSurvey} from "~/composables/useSurvey"
import type SurveyPayload from "~/services/SurveyService/interfaces/SurveyPayload"
import type SurveyQuestion from "~/services/SurveyService/interfaces/SurveyQuestion"
import type SurveyConsent from "~/services/SurveyService/interfaces/SurveyConsent"

const props = defineProps({
  type: {type: String, required: true},
  payload: {type: Object, required: false},
  parentIdent: {type: String, required: false},
})

// Default to top is instant
const { scrollToAnchor } = useAnchorScroll({
  toAnchor: {
    scrollOptions: {
      behavior: 'smooth',
      offsetTop: -150,
    }
  },
})

const surveyAnchorId = `survey_${props.payload?.id}`

const ANSWER_PREFIX = "odpoved"
const CORRECT_ANSWER_PREFIXES = ["spravnaOdpoved"]

const {t} = useI18n()
const logger = useFrontendLogger()
const route = useRoute()
const { executeRecaptcha  } = useGoogleRecaptcha()

const submitSuccess = ref(false)
const submitError = ref(false)
const isLoading = ref(false)
const submitErrors: any[] = reactive([])

const isSurveySubmitted = ref(false)
isSurveySubmitted.value = useSurvey().isSurveySubmitted(props.payload?.id)

const isDisabled = computed(() => {
  return isLoading.value || isSurveySubmitted.value
})

const validate = (state: any): FormError[] => {
  const validRegex = new RegExp("^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$", "i")
  const errors = []

  for (let answer in state.answers) {
    if (state?.answers[answer]?.constructor === Object) {
      let isValid = false
      for (let prop in state?.answers[answer]) {
        if (state?.answers[answer][prop]) {
          isValid = true
        }
      }
      if (!isValid) {
        errors.push({ path: `question.${answer}`, message: t('forms.required') })
      }
    } else if (!state.answers[answer]) {
      errors.push({ path: `question.${answer}`, message: t('forms.required') })
    }
  }

  if (!state.fullname) {
    errors.push({ path: 'fullname', message: t('forms.required') })
  }

  if (!state.email) {
    errors.push({ path: 'email', message: t('forms.required') })
  }
  if (state.email && !state.email.match(validRegex)) {
    errors.push({ path: 'email', message: t('forms.invalid_email') })
  }

  if (!state.consents.terms) {
    errors.push({ path: 'terms', message: t('forms.required') })
  }
  // if (!state.consents.gdpr) {
  //   errors.push({ path: 'gdpr', message: t('forms.required') })
  // }

  if (errors.length > 0) {
    scrollToAnchor(`${surveyAnchorId}.${errors[0].path}`)
  }

  return errors
}

async function onSubmit(event: FormSubmitEvent<any>) {
  submitSuccess.value = false
  submitError.value = false
  isLoading.value = true
  submitErrors.splice(0, submitErrors.length)

  // recaptcha
  const { token } = await executeRecaptcha('submit')

  // prepare payload
  const surveyPayload = getSurveySubmitPayload(event.data, token)
  const result = await useSurvey().submitSurvey(surveyPayload)

  if (result.status === 'error') {
    if (result.statusCode === 409) {
      // Already submited
      isSurveySubmitted.value = true
    } else if (result.errors) {
      // validation error
      result.errors.forEach((error: any) => {
        submitErrors.push(error)
      })
    } else {
      // server error
      submitError.value = true
    }
  } else {
    submitSuccess.value = true
    useSurvey().setSurveySubmitted(props.payload?.id)
    scrollToAnchor(surveyAnchorId)
  }
  isLoading.value = false
}

const getTitle = () => {
  return props.payload?.data?.nazev
}

const getQuestions = () => {
  const questions = []
  for (let index in props.payload?.data?.otazky) {
    questions.push({
      id: index,
      question: props.payload?.data?.otazky[index].soutezniOtazka,
      answers: getAnswers(props.payload?.data?.otazky[index]),
      originalAnswers: props.payload?.data?.otazky[index]
    })
  }
  return questions
}

const getAnswers = (answers: Record<string, string>) => {
  const filteredAnswers = []
  for (let index in answers) {
    if (index.match(new RegExp(`^${ANSWER_PREFIX}`, "gi"))) {
      const isCorrect = isAnswerCorrect(index, answers)
      filteredAnswers.push({
        value: index,
        label: answers[index],
        isCorrect
      })
    }
  }
  return filteredAnswers
}

const isAnswerCorrect = (answerValue: string, answers: Record<string, any>): boolean => {
  const valueChar = answerValue.substring(ANSWER_PREFIX.length, answerValue.length)
  let isCorrect = false
  for (let index in answers) {
    const answerIndexes = CORRECT_ANSWER_PREFIXES.map((prefix) => {
      return `${prefix}${valueChar}`
    })
    if (answerIndexes.includes(index)) {
      isCorrect = answers[index] === true
      break
    }
  }
  return isCorrect
}

const isMultipleAnswerAvailable = (answers: Record<string, any>): boolean => {
  let correctAnswers = 0
  for (let index in answers) {
    if (index.match(new RegExp(`^(${CORRECT_ANSWER_PREFIXES.join('|')})`, "gi"))) {
      if (answers[index] === true) {
        correctAnswers = correctAnswers + 1
      }
    }
  }
  return correctAnswers > 1
}

const getModel = () => {
  const ids: any = {}
  getQuestions().forEach((question) => {
    const isCheckbox = isMultipleAnswerAvailable(question.originalAnswers)
    if (!isCheckbox) {
      ids[question.id] = undefined
    } else {
      ids[question.id] = {}
      question.answers.forEach((answer) => {
        ids[question.id][answer.value] = undefined
      })
    }
  })
  return ids
}

const getSurveySubmitPayload = (data: any, recaptchaToken?: string): SurveyPayload => {

  const consents: SurveyConsent[] = [
    {
      name: 'terms',
      value: !!data.consents.terms
    },
    {
      name: 'gdpr',
      value: !!data.consents.gdpr
    }
  ]

  const questions: SurveyQuestion[] = []
  getQuestions().forEach((question) => {
    // const answer: SurveyQuestionAnswer | undefined = question.answers.find((answer) => state.answers[question.id] === answer.value)
    const isCheckbox = isMultipleAnswerAvailable(question.originalAnswers)

    const answers = question.answers.map((answer) => {
      let isSelected = false
      if (!isCheckbox && state.answers[question.id] === answer.value) {
        isSelected = true
      } else if (isCheckbox && state.answers[question.id][answer.value]) {
        isSelected = true
      }

      return {
        isSelected,
        value: typeof answer?.value !== 'undefined' ? String(answer?.value) : '',
        label: typeof answer?.label !== 'undefined' ? String(answer?.label) : '',
        isCorrect: typeof answer?.isCorrect !== 'undefined' ? !!answer?.isCorrect : false,
      }
    })

    questions.push({
      answers,
      id: question.id,
      question: question.question,
    })
  })

  return {
    recaptchaToken,
    consents,
    questions,
    surveyUrl: window?.location?.href || route.path,
    surveyId: props.payload?.id,
    surveyName: getTitle(),
    fullname: data.fullname,
    email: data.email,
  }
}

const state = reactive({
  email: undefined,
  fullname: undefined,
  consents: {
    terms: undefined,
    gdpr: undefined,
  },
  answers: getModel()
})

if (!props.payload?.data?.otazky) {
  // log to sentry
  logger?.addTag('artObject.type', props.type || 'unknown').info('Survey questions not defined', {
    ArtObjectType: props.type,
    ParentIdent: props.parentIdent,
    SurveyId: props.payload?.id,
  })
}
</script>